import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Award,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Check,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Handshake,
  Home,
  MapPin,
  MessageCircle,
  Pencil,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Trophy,
  UserRoundSearch,
  UsersRound,
  X
} from 'lucide-react';
import { api, haptic, initTelegram, notify, openTelegram } from './api.js';
import { industries, speakers } from './data.js';

const eventDate = new Date('2026-07-23T10:00:00+03:00');

function daysUntilEvent() {
  const days = Math.ceil((eventDate.getTime() - Date.now()) / 86400000);
  if (days > 1) return `До форума ${days} дней`;
  if (days === 1) return 'Форум уже завтра';
  if (days === 0) return 'Форум уже сегодня';
  return 'Форум завершён';
}

function personName(person) {
  return [person.firstName, person.lastName].filter(Boolean).join(' ');
}

function initials(person) {
  return [person.firstName, person.lastName]
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toLocaleUpperCase('ru') || 'Q';
}

function App() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('home');
  const [onboarding, setOnboarding] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [toast, setToast] = useState('');

  useEffect(() => {
    initTelegram();
    api('/api/bootstrap')
      .then((payload) => {
        setData(payload);
        setOnboarding(!payload.me.profileComplete);
      })
      .catch((caught) => setError(caught.message));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 3500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  async function saveProfile(profile) {
    const payload = await api('/api/me', {
      method: 'PUT',
      body: JSON.stringify(profile)
    });
    setData((current) => ({ ...current, me: payload.profile, matches: payload.matches }));
    setOnboarding(false);
    setEditingProfile(false);
    setToast('Профиль сохранён. Подбор обновлён.');
    notify('success');
  }

  async function setMatchAction(match, status) {
    haptic(status === 'met' ? 'heavy' : 'medium');
    const payload = await api(`/api/matches/${match.id}/action`, {
      method: 'POST',
      body: JSON.stringify({ status })
    });
    setData((current) => ({
      ...current,
      stats: payload.stats,
      me: { ...current.me, points: payload.points },
      matches: current.matches.map((item) => item.id === match.id ? { ...item, action: status } : item)
    }));
    setSelectedMatch((current) => current?.id === match.id ? { ...current, action: status } : current);
    setToast(status === 'met' ? 'Встреча подтверждена: +25 баллов' : 'Запрос на знакомство сохранён');
  }

  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;
  if (!data) return <LoadingScreen />;

  const screens = {
    home: <HomeScreen data={data} onCompleteProfile={() => setOnboarding(true)} onSelectMatch={setSelectedMatch} onNavigate={setTab} />,
    matches: <MatchesScreen matches={data.matches} profileComplete={data.me.profileComplete} onCompleteProfile={() => setOnboarding(true)} onSelect={setSelectedMatch} />,
    program: <ProgramScreen />,
    profile: <ProfileScreen me={data.me} onEdit={() => setEditingProfile(true)} />
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main">К основному содержимому</a>
      <Header me={data.me} />
      <main id="main" className="main-content" tabIndex="-1">
        {screens[tab]}
      </main>
      <BottomNavigation current={tab} onChange={setTab} />

      {(onboarding || editingProfile) && (
        <Onboarding
          initial={data.me}
          editMode={editingProfile}
          onClose={() => {
            setOnboarding(false);
            setEditingProfile(false);
          }}
          onSave={saveProfile}
        />
      )}
      {selectedMatch && (
        <MatchSheet
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          onAction={(status) => setMatchAction(selectedMatch, status)}
        />
      )}
      <div className={`toast ${toast ? 'toast--visible' : ''}`} role="status" aria-live="polite">
        <Check size={18} aria-hidden="true" />
        {toast}
      </div>
    </div>
  );
}

function Header({ me }) {
  return (
    <header className="topbar">
      <div className="brand" aria-label="QUATRUM Connect">
        <span className="brand__mark">Q</span>
        <span className="brand__wordmark">QUATRUM <b>CONNECT</b></span>
      </div>
      <div className="mini-avatar" aria-label={`Профиль: ${personName(me)}`}>{initials(me)}</div>
    </header>
  );
}

function HomeScreen({ data, onCompleteProfile, onSelectMatch, onNavigate }) {
  const bestMatch = data.matches[0];
  return (
    <div className="screen screen--home">
      <section className="event-hero">
        <div className="event-hero__eyebrow"><CalendarDays size={16} /> Путь к миллиарду 2.0</div>
        <h1>Ваш день сильных знакомств</h1>
        <p>Подскажем, с кем встретиться на форуме и почему вы можете быть полезны друг другу.</p>
        <div className="event-hero__meta">
          <span><CalendarDays size={17} />23 июля</span>
          <span><Clock3 size={17} />10:00–18:00</span>
          <span><MapPin size={17} />Казань</span>
        </div>
        <div className="event-hero__countdown">{daysUntilEvent()}</div>
      </section>

      {!data.me.profileComplete ? (
        <section className="mission-card mission-card--accent">
          <div className="section-icon"><Target size={22} /></div>
          <div className="mission-card__content">
            <span className="overline">Первая миссия</span>
            <h2>Расскажите, кого вы ищете</h2>
            <p>За 2 минуты соберём профиль и покажем наиболее полезные знакомства.</p>
            <button className="button button--primary" type="button" onClick={onCompleteProfile}>
              Заполнить профиль <ArrowRight size={18} />
            </button>
          </div>
        </section>
      ) : bestMatch ? (
        <section>
          <SectionHeader eyebrow="Ваш лучший контакт" title="Начните с этого знакомства" action="Все контакты" onAction={() => onNavigate('matches')} />
          <MatchCard match={bestMatch} featured onSelect={() => onSelectMatch(bestMatch)} />
        </section>
      ) : null}

      <section>
        <SectionHeader eyebrow="Ваш прогресс" title="Результат форума" />
        <div className="stats-grid">
          <StatCard icon={Award} value={data.stats.points} label="баллов" />
          <StatCard icon={MessageCircle} value={data.stats.requests} label="запросов" />
          <StatCard icon={Handshake} value={data.stats.meetings} label="встреч" />
        </div>
      </section>

      <section>
        <SectionHeader eyebrow="Топ участников" title="Лидеры нетворкинга" />
        <div className="leaderboard-card">
          {data.leaderboard.slice(0, 3).map((person, index) => (
            <div className="leader-row" key={person.id}>
              <span className={`leader-row__place place-${index + 1}`}>{index + 1}</span>
              <div className="avatar avatar--small">{initials(person)}</div>
              <div className="leader-row__person">
                <strong>{personName(person)}</strong>
                <span>{person.company || person.industry}</span>
              </div>
              <span className="leader-row__points">{person.points}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function MatchesScreen({ matches, profileComplete, onCompleteProfile, onSelect }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    if (!needle) return matches;
    return matches.filter((match) => [personName(match), match.company, match.industry, match.offers, match.needs]
      .join(' ')
      .toLocaleLowerCase('ru')
      .includes(needle));
  }, [matches, query]);

  return (
    <div className="screen">
      <PageTitle eyebrow="Умный подбор" title="Полезные знакомства" description="Чем точнее профиль, тем сильнее рекомендации." />
      {!profileComplete && (
        <button className="profile-nudge" type="button" onClick={onCompleteProfile}>
          <Sparkles size={20} />
          <span><strong>Улучшить подбор</strong><small>Заполните, что ищете и чем полезны</small></span>
          <ChevronRight size={20} />
        </button>
      )}
      <label className="search-field">
        <span className="sr-only">Поиск по участникам</span>
        <Search size={19} aria-hidden="true" />
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Имя, компания или сфера" />
      </label>
      <div className="matches-list">
        {filtered.map((match) => <MatchCard key={match.id} match={match} onSelect={() => onSelect(match)} />)}
        {!filtered.length && <EmptyState icon={UserRoundSearch} title="Ничего не нашли" text="Попробуйте изменить запрос." />}
      </div>
    </div>
  );
}

function MatchCard({ match, featured = false, onSelect }) {
  return (
    <button className={`match-card ${featured ? 'match-card--featured' : ''}`} type="button" onClick={onSelect}>
      <div className="match-card__top">
        <div className="avatar">{initials(match)}</div>
        <div className="match-card__identity">
          <div className="match-card__name-line">
            <h3>{personName(match)}</h3>
            {match.isDemo && <span className="demo-badge">Демо</span>}
          </div>
          <p>{match.role}{match.company ? ` · ${match.company}` : ''}</p>
        </div>
        <span className="match-score" aria-label={`Совпадение ${match.score} процентов`}>{match.score}%</span>
      </div>
      <div className="match-card__tags">
        <span><Building2 size={15} />{match.industry}</span>
        {match.city && <span><MapPin size={15} />{match.city}</span>}
      </div>
      <div className="match-card__reason">
        <Sparkles size={17} />
        <span>{match.reasons[0]}</span>
      </div>
      <div className="match-card__footer">
        <span>{match.action === 'met' ? 'Вы познакомились' : match.action === 'requested' ? 'Запрос отправлен' : 'Посмотреть профиль'}</span>
        {match.action === 'met' ? <Check size={19} /> : <ChevronRight size={19} />}
      </div>
    </button>
  );
}

function MatchSheet({ match, onClose, onAction }) {
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="match-title">
        <div className="sheet-handle" />
        <button className="icon-button sheet-close" type="button" onClick={onClose} aria-label="Закрыть профиль"><X size={22} /></button>
        <div className="sheet-profile">
          <div className="avatar avatar--large">{initials(match)}</div>
          <div>
            <div className="match-card__name-line">
              <h2 id="match-title">{personName(match)}</h2>
              {match.isDemo && <span className="demo-badge">Демо</span>}
            </div>
            <p>{match.role} · {match.company}</p>
          </div>
          <span className="match-score">{match.score}%</span>
        </div>
        {match.bio && <p className="sheet-bio">{match.bio}</p>}
        <div className="value-block">
          <span><Target size={18} />Ищет</span>
          <p>{match.needs || 'Пока не указано'}</p>
        </div>
        <div className="value-block">
          <span><BriefcaseBusiness size={18} />Может помочь</span>
          <p>{match.offers || 'Пока не указано'}</p>
        </div>
        <div className="why-block">
          <span className="overline">Почему вам по пути</span>
          {match.reasons.map((reason) => <p key={reason}><Check size={17} />{reason}</p>)}
        </div>
        <div className="sheet-actions">
          {match.username && (
            <button className="button button--secondary" type="button" onClick={() => openTelegram(match.username)}>
              <MessageCircle size={19} /> Написать
            </button>
          )}
          <button
            className="button button--primary"
            type="button"
            onClick={() => onAction(match.action === 'requested' ? 'met' : match.action === 'met' ? 'met' : 'requested')}
            disabled={match.action === 'met'}
          >
            {match.action === 'met' ? <><Check size={19} /> Встреча подтверждена</> : match.action === 'requested' ? <><Handshake size={19} /> Мы познакомились</> : <><UsersRound size={19} /> Хочу познакомиться</>}
          </button>
        </div>
      </section>
    </div>
  );
}

function ProgramScreen() {
  return (
    <div className="screen">
      <PageTitle eyebrow="23 июля · Казань" title="Программа форума" description="Шесть предпринимателей. Один насыщенный день." />
      <section className="venue-card">
        <div className="section-icon"><MapPin size={22} /></div>
        <div><span className="overline">Площадка</span><h2>ИТ-парк им. Башира Рамеева</h2><p>10:00–18:00 · полный день</p></div>
      </section>
      <SectionHeader eyebrow="На сцене" title="Спикеры форума" />
      <div className="speaker-list">
        {speakers.map((speaker, index) => (
          <article className="speaker-card" key={speaker.name}>
            <div className="speaker-avatar">{speaker.initials}</div>
            <div><span className="speaker-number">0{index + 1}</span><h3>{speaker.name}</h3><p>{speaker.role}</p></div>
          </article>
        ))}
      </div>
      <div className="info-note"><Clock3 size={19} /><p>Подробный тайминг появится после подтверждения организаторами.</p></div>
    </div>
  );
}

function ProfileScreen({ me, onEdit }) {
  return (
    <div className="screen">
      <PageTitle eyebrow="Ваша визитка" title="Профиль участника" description="Эти данные помогают находить релевантные знакомства." />
      <section className="profile-card">
        <div className="profile-card__header">
          <div className="avatar avatar--xlarge">{initials(me)}</div>
          <div><h2>{personName(me)}</h2><p>{me.role || 'Роль не указана'}{me.company ? ` · ${me.company}` : ''}</p></div>
          <button className="icon-button" type="button" onClick={onEdit} aria-label="Редактировать профиль"><Pencil size={20} /></button>
        </div>
        <div className="profile-meta">
          {me.industry && <span><Building2 size={17} />{me.industry}</span>}
          {me.city && <span><MapPin size={17} />{me.city}</span>}
        </div>
        {me.bio && <p className="profile-bio">{me.bio}</p>}
        <div className="profile-values">
          <div><span><Target size={17} />Ищу</span><p>{me.needs || 'Пока не указано'}</p></div>
          <div><span><BriefcaseBusiness size={17} />Могу помочь</span><p>{me.offers || 'Пока не указано'}</p></div>
        </div>
      </section>
      <section className="privacy-card">
        <ShieldCheck size={23} />
        <div><h3>Контакты под контролем</h3><p>{me.consentShare ? 'Telegram станет доступен после взаимного интереса.' : 'Telegram не показывается другим участникам.'}</p></div>
      </section>
      <button className="button button--secondary button--wide" type="button" onClick={onEdit}><Pencil size={18} />Редактировать профиль</button>
    </div>
  );
}

function Onboarding({ initial, editMode, onClose, onSave }) {
  const [step, setStep] = useState(editMode ? 1 : 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    firstName: initial.firstName || '',
    lastName: initial.lastName || '',
    company: initial.company || '',
    role: initial.role || '',
    industry: initial.industry || '',
    city: initial.city || '',
    bio: initial.bio || '',
    offers: initial.offers || '',
    needs: initial.needs || '',
    consentShare: initial.consentShare || false
  });

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError('');
  }

  function next() {
    if (step === 1 && (!form.firstName.trim() || !form.company.trim() || !form.role.trim() || !form.industry)) {
      setError('Заполните имя, компанию, роль и сферу бизнеса.');
      return;
    }
    if (step === 2 && !form.offers.trim()) {
      setError('Расскажите хотя бы в двух словах, чем можете помочь.');
      return;
    }
    setStep((current) => Math.min(3, current + 1));
    haptic('light');
  }

  async function submit() {
    if (!form.needs.trim()) {
      setError('Укажите, кого или что хотите найти на форуме.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form);
    } catch (caught) {
      setError(caught.message);
      setSaving(false);
    }
  }

  return (
    <div className="onboarding" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
      <div className="onboarding__topbar">
        {step > 0 ? (
          <button className="icon-button" type="button" onClick={() => setStep((current) => current - 1)} aria-label="Назад"><ArrowLeft size={22} /></button>
        ) : <span />}
        <div className="progress-dots" aria-label={`Шаг ${Math.max(step, 1)} из 3`}>
          {[1, 2, 3].map((value) => <span key={value} className={step >= value ? 'active' : ''} />)}
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Закрыть"><X size={22} /></button>
      </div>

      <div className="onboarding__content">
        {step === 0 && (
          <div className="welcome-step">
            <div className="welcome-mark"><Handshake size={38} /></div>
            <span className="overline">QUATRUM Connect</span>
            <h1 id="onboarding-title">Главное знакомство может случиться здесь</h1>
            <p>Расскажите о себе — мы предложим людей, с которыми есть предметный повод поговорить.</p>
            <div className="welcome-benefits">
              <span><Check size={18} />Только релевантные контакты</span>
              <span><Check size={18} />Профиль займёт около 2 минут</span>
              <span><ShieldCheck size={18} />Контакты — только с вашего согласия</span>
            </div>
            <button className="button button--primary button--wide" type="button" onClick={() => setStep(1)}>Начать <ArrowRight size={19} /></button>
            <button className="text-button" type="button" onClick={onClose}>Посмотреть приложение</button>
          </div>
        )}

        {step === 1 && (
          <FormStep title="Давайте познакомимся" description="Основа вашей цифровой визитки.">
            <div className="form-grid form-grid--two">
              <Field label="Имя *"><input value={form.firstName} onChange={(e) => update('firstName', e.target.value)} autoComplete="given-name" /></Field>
              <Field label="Фамилия"><input value={form.lastName} onChange={(e) => update('lastName', e.target.value)} autoComplete="family-name" /></Field>
            </div>
            <Field label="Компания *"><input value={form.company} onChange={(e) => update('company', e.target.value)} autoComplete="organization" placeholder="Название бизнеса" /></Field>
            <Field label="Ваша роль *"><input value={form.role} onChange={(e) => update('role', e.target.value)} autoComplete="organization-title" placeholder="Собственник, CEO, руководитель" /></Field>
            <Field label="Сфера бизнеса *"><select value={form.industry} onChange={(e) => update('industry', e.target.value)}><option value="">Выберите сферу</option>{industries.map((value) => <option key={value}>{value}</option>)}</select></Field>
            <Field label="Город"><input value={form.city} onChange={(e) => update('city', e.target.value)} autoComplete="address-level2" placeholder="Казань" /></Field>
          </FormStep>
        )}

        {step === 2 && (
          <FormStep title="Чем вы можете помочь" description="Конкретика даст другим участникам повод начать разговор.">
            <Field label="Ваши ресурсы и экспертиза *" helper="Например: внедряю CRM, ищу инвестиции для проектов, вывожу бренды на маркетплейсы.">
              <textarea value={form.offers} onChange={(e) => update('offers', e.target.value)} rows="5" maxLength="500" placeholder="Я могу помочь с…" />
            </Field>
            <Field label="Коротко о себе" helper="Необязательно, до 500 знаков.">
              <textarea value={form.bio} onChange={(e) => update('bio', e.target.value)} rows="4" maxLength="500" placeholder="Чем занимается ваш бизнес" />
            </Field>
          </FormStep>
        )}

        {step === 3 && (
          <FormStep title="Кого вы хотите найти" description="Это главный сигнал для алгоритма подбора.">
            <Field label="Моя цель на форуме *" helper="Например: партнёр в Москве, руководитель отдела продаж, инвестор, поставщик.">
              <textarea value={form.needs} onChange={(e) => update('needs', e.target.value)} rows="6" maxLength="500" placeholder="Я ищу…" />
            </Field>
            <label className="consent-row">
              <input type="checkbox" checked={form.consentShare} onChange={(e) => update('consentShare', e.target.checked)} />
              <span><strong>Разрешить обмен Telegram</strong><small>Контакт откроется только после взаимного интереса.</small></span>
            </label>
          </FormStep>
        )}

        {error && <p className="form-error" role="alert">{error}</p>}
      </div>

      {step > 0 && (
        <div className="onboarding__footer">
          {step < 3 ? (
            <button className="button button--primary button--wide" type="button" onClick={next}>Продолжить <ArrowRight size={19} /></button>
          ) : (
            <button className="button button--primary button--wide" type="button" onClick={submit} disabled={saving}>
              {saving ? <span className="spinner" /> : <Sparkles size={19} />}{saving ? 'Подбираем контакты…' : 'Найти знакомства'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FormStep({ title, description, children }) {
  return <div className="form-step"><span className="overline">Профиль участника</span><h1 id="onboarding-title">{title}</h1><p className="form-step__description">{description}</p><div className="form-fields">{children}</div></div>;
}

function Field({ label, helper, children }) {
  return <label className="field"><span>{label}</span>{children}{helper && <small>{helper}</small>}</label>;
}

function BottomNavigation({ current, onChange }) {
  const items = [
    { id: 'home', label: 'Главная', icon: Home },
    { id: 'matches', label: 'Знакомства', icon: UsersRound },
    { id: 'program', label: 'Программа', icon: CalendarDays },
    { id: 'profile', label: 'Профиль', icon: CircleUserRound }
  ];
  return (
    <nav className="bottom-nav" aria-label="Основная навигация">
      {items.map(({ id, label, icon: Icon }) => (
        <button key={id} className={current === id ? 'active' : ''} type="button" onClick={() => { onChange(id); haptic('light'); }} aria-current={current === id ? 'page' : undefined}>
          <Icon size={22} strokeWidth={current === id ? 2.2 : 1.8} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function PageTitle({ eyebrow, title, description }) {
  return <header className="page-title"><span className="overline">{eyebrow}</span><h1>{title}</h1><p>{description}</p></header>;
}

function SectionHeader({ eyebrow, title, action, onAction }) {
  return <header className="section-header"><div><span className="overline">{eyebrow}</span><h2>{title}</h2></div>{action && <button type="button" onClick={onAction}>{action}<ChevronRight size={17} /></button>}</header>;
}

function StatCard({ icon: Icon, value, label }) {
  return <div className="stat-card"><Icon size={20} /><strong>{value}</strong><span>{label}</span></div>;
}

function EmptyState({ icon: Icon, title, text }) {
  return <div className="empty-state"><Icon size={30} /><h3>{title}</h3><p>{text}</p></div>;
}

function LoadingScreen() {
  return <div className="loading-screen"><div className="brand brand--center"><span className="brand__mark">Q</span><span className="brand__wordmark">QUATRUM <b>CONNECT</b></span></div><div className="loading-line"><span /></div><p>Готовим полезные знакомства</p></div>;
}

function ErrorState({ message, onRetry }) {
  return <div className="error-state"><div className="section-icon"><X size={24} /></div><h1>Не удалось открыть приложение</h1><p>{message}</p><button className="button button--primary" type="button" onClick={onRetry}>Попробовать снова</button></div>;
}

export default App;
